import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CGU from './CGU';
import './CGUModal.css';

const CGUModal = ({ isOpen, onAccept, onDecline, userEmail }) => {
  const { t } = useTranslation();
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [acceptEnabled, setAcceptEnabled] = useState(false);

  useEffect(() => {
    // Enable accept button after 10 seconds or if user has read to the bottom
    const timer = setTimeout(() => {
      setAcceptEnabled(true);
    }, 10000); // 10 secondes

    return () => clearTimeout(timer);
  }, [isOpen]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 50;
    
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
      setAcceptEnabled(true);
    }
  };

  const handleAccept = () => {
    // Mark CGU as accepted in localStorage
    localStorage.setItem('cgu_accepted', 'true');
    localStorage.setItem('cgu_accepted_date', new Date().toISOString());
    localStorage.setItem('cgu_accepted_user', userEmail);
    localStorage.setItem('cgu_version', '1.0');
    
    onAccept();
  };

  const handleDecline = () => {
    // Remove authentication data
    localStorage.removeItem('authData');
    localStorage.removeItem('cgu_accepted');
    
    onDecline();
  };

  if (!isOpen) return null;

  return (
    <div className="cgu-modal-overlay">
      <div className="cgu-modal-container">
        <div className="cgu-modal-header">
          <h2>{t('cguModal.title')}</h2>
          <p>{t('cguModal.description')}</p>
          {!acceptEnabled && (
            <div className="reading-notice">
              <div className="notice-content">
                <span className="notice-icon">👁️</span>
                <span className="notice-text">
                  {t('cguModal.readingNotice')}
                </span>
              </div>
              <div className="reading-progress">
                <div className="progress-indicator"></div>
              </div>
            </div>
          )}
        </div>
        
        <div className="cgu-modal-content" onScroll={handleScroll}>
          <CGU 
            onAccept={handleAccept} 
            onDecline={handleDecline}
            showButtons={false}
          />
        </div>

        <div className="cgu-modal-actions">
          <div className="actions-info">
            <span className="user-info">👤 {userEmail}</span>
            <span className="version-info">{t('cguModal.version')} - {t('cguModal.updateDate')}</span>
          </div>
          
          <div className="actions-buttons">
            <button 
              className="btn-modal-decline" 
              onClick={handleDecline}
            >
              ❌ {t('cguModal.decline')}
            </button>
            <button 
              className={`btn-modal-accept ${acceptEnabled ? 'enabled' : 'disabled'}`}
              onClick={handleAccept}
              disabled={!acceptEnabled}
            >
              {acceptEnabled ? '✅ ' + t('cguModal.accept') : '⏳ ' + t('cguModal.reading')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Hook to check if CGU have been accepted
export const useCGUStatus = (userEmail) => {
  const [cguAccepted, setCguAccepted] = useState(false);
  const [cguRequired, setCguRequired] = useState(false);

  useEffect(() => {
    const checkCGUStatus = () => {
      const accepted = localStorage.getItem('cgu_accepted') === 'true';
      const acceptedUser = localStorage.getItem('cgu_accepted_user');
      const acceptedVersion = localStorage.getItem('cgu_version');
      
      // Check if it's the same user and same version
      const isValidAcceptance = accepted && 
                               acceptedUser === userEmail && 
                               acceptedVersion === '1.0';
      
      setCguAccepted(isValidAcceptance);
      setCguRequired(!isValidAcceptance && !!userEmail);
    };

    checkCGUStatus();
  }, [userEmail]);

  const markCGUAsAccepted = () => {
    setCguAccepted(true);
    setCguRequired(false);
  };

  const requireCGUAcceptance = () => {
    setCguAccepted(false);
    setCguRequired(true);
  };

  return {
    cguAccepted,
    cguRequired,
    markCGUAsAccepted,
    requireCGUAcceptance
  };
};

export default CGUModal;
